import type { RevenueAnalysisDimension } from '@/shared/api/modules/facilities';

/**
 * Left tab group: what the chart is split by. `forecasted` has no split at all —
 * it is total revenue plus a projection — which is why it is not part of the
 * API's `RevenueAnalysisDimension` union and gets its own endpoint.
 */
export const REVENUE_TABS = [
  'forecasted',
  'payerType',
  'levelsOfCare',
  'medicaidInsurer',
] as const;

export type RevenueTab = (typeof REVENUE_TABS)[number];

export const REVENUE_TAB_LABELS: Record<RevenueTab, string> = {
  forecasted: 'Forecasted',
  payerType: 'Payer Type',
  levelsOfCare: 'Levels of Care',
  medicaidInsurer: 'Medicaid Insurer',
};

/** Right tab group: which number the same months are expressed in. */
export const REVENUE_METRICS = ['revenue', 'revenuePerResident'] as const;

export type RevenueMetric = (typeof REVENUE_METRICS)[number];

export const REVENUE_METRIC_LABELS: Record<RevenueMetric, string> = {
  revenue: 'Revenue',
  // Lower-case "resident" is the tab; the card title is title-cased. Both per Figma.
  revenuePerResident: 'Revenue per resident',
};

export const REVENUE_METRIC_TITLES: Record<RevenueMetric, string> = {
  revenue: 'Revenue Analysis',
  revenuePerResident: 'Revenue per Resident Analysis',
};

export const isDimensionTab = (
  tab: RevenueTab
): tab is RevenueAnalysisDimension => tab !== 'forecasted';

/** One KPI card and its matching chart series, already reduced to what both need. */
export type RevenueAnalysisSeriesView = {
  key: string;
  label: string;
  color: string;
  /** Card background wash, Revenue metric only. */
  softColor: string;
  /**
   * One value per month in the window, in the metric currently selected.
   *
   * `null` means "no value this month", which only the per-resident metric can
   * produce: with nobody billed there is no average, and plotting a 0 would draw
   * a collapse to the axis that never happened. Under Revenue a missing month is
   * a genuine zero.
   */
  values: (number | null)[];
  /** The single figure on the KPI card. */
  total: number;
  /** Share of the leading card, Revenue metric only. Null when it means nothing. */
  sharePct: number | null;
};
